// TASK-901 — the rebalancing proposal, as the canvas sees it.
//
// TASK-868 built this engine and nothing on the canvas could reach it. Three prompts asked for
// it: two were refused honestly ("there's no view for it yet") and the third opened `Swaps`,
// which states in its own copy that sub-asset-class weights are identical before and after, and
// claimed it brought the slot back to target (TASK-899).
//
// Two endpoints, deliberately apart, and the split is load-bearing: `POST /rebalance-proposal`
// BUILDS and does not persist, `POST /proposals` saves. Opening a breached slot to see the
// consequence must not itself be a record.
//
// The field names here are the ones the service actually returns, verified live against Julian
// Ammann on 2026-09-01. TASK-901's body describes `transactions[]` with `current_metrics` /
// `proposed_metrics`; the engine returns `rows[]` with `drift_before` / `drift_after`. Written
// against the endpoint rather than the ticket, because the ticket is what the endpoint was
// described as, and the endpoint is what renders.
import { httpClient } from '../lib/httpClient';

/** One position the proposal would trim. Keyed by `position_id` so a colleague can be told
 *  "check row 12" and land on the same row (TASK-876). */
export interface TrimRow {
  position_id: string;
  isin: string | null;
  security_name: string | null;
  slot: string;
  currency: string;
  current_value_chf: number;
  trim_chf: number;
  post_value_chf: number;
  /** false ⇒ this row is sized in CHF and no share count is available. The widget says so
   *  rather than inventing a quantity — the defect TASK-870 was written for. */
  share_sizing_available: boolean;
}

export interface SlotMetric {
  current: number;
  target: number;
  drift: number;
  breach: boolean;
  asset_class: string;
}

export interface DriftSnapshot {
  by_slot: Record<string, SlotMetric>;
  max_drift_pct: number;
  total_portfolio_value: number;
}

export interface RebalanceProposal {
  client_id: string;
  portfolio_id: string | null;
  /** The slot the proposal was asked to bring back to target. */
  slot: string;
  sizing_unit: string;
  share_sizing_available: boolean;
  trim_chf: number;
  shortfall_chf: number;
  slot_value_chf: number;
  total_portfolio_value: number;
  rows: TrimRow[];
  drift_before: DriftSnapshot;
  drift_after: DriftSnapshot;
  /** Slot names in breach BEFORE and AFTER. Computed server-side by applying the trims to a
   *  deep copy and re-running the calculator — never narrated. The widget renders these; it
   *  does not re-derive them, because two derivations are how the two disagree. */
  breaches_before: string[];
  breaches_after: string[];
  /** Slots that are in breach AFTER and were not before. The case the old assistant stamped
   *  "✅ The drift breach would be resolved" over. */
  new_breaches: string[];
  slot_was_breached: boolean;
  resolves_target_slot: boolean;
  opens_new_breach: boolean;
  calculation_version: number;
  tolerance_pct: number;
  timestamp: string;
}

export interface SavedProposalsResponse {
  proposals: unknown[];
  count: number;
  timestamp: string;
}

export const rebalanceService = {
  /** Build a proposal for `slot` WITHOUT persisting it.
   *
   *  Generated on demand, which is what makes the widget reachable for every client: Schneider
   *  had a stored proposal and Ammann had none, so a read-only view would have been empty for
   *  the client the defect was reported against. */
  build(clientId: string, slot: string): Promise<RebalanceProposal> {
    return httpClient.post<RebalanceProposal>(
      `/api/portfolio/rebalance-proposal/${clientId}`,
      { slot },
    );
  },

  /** Persist a built proposal. Separate call, separate intent — see the note above. */
  save(clientId: string, proposal: RebalanceProposal): Promise<{ id?: string }> {
    return httpClient.post<{ id?: string }>(
      `/api/portfolio/proposals/${clientId}`,
      proposal,
    );
  },

  list(clientId: string): Promise<SavedProposalsResponse> {
    return httpClient.get<SavedProposalsResponse>(`/api/portfolio/proposals/${clientId}`);
  },
};

/** The slot a proposal should target: the worst breaching slot, else the worst drift.
 *
 *  Pure so the widget and its tests agree on it. Returns null when nothing is in breach and
 *  nothing has drifted — there is no proposal to make, and inventing a target slot would be a
 *  trade recommendation with no reason behind it. */
export function worstDriftedSlot(drift: DriftSnapshot | null | undefined): string | null {
  const bySlot = drift?.by_slot;
  if (!bySlot) return null;
  const entries = Object.entries(bySlot);
  if (entries.length === 0) return null;
  const breached = entries.filter(([, m]) => m.breach);
  const pool = breached.length > 0 ? breached : entries;
  let best: [string, SlotMetric] | null = null;
  for (const entry of pool) {
    if (best === null || Math.abs(entry[1].drift) > Math.abs(best[1].drift)) best = entry;
  }
  if (best === null) return null;
  if (breached.length === 0 && Math.abs(best[1].drift) === 0) return null;
  return best[0];
}
